import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PORTRAITS, IDENTITIES, ORIGINS, QUIZ } from "@/constants/assets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Heart, Flame, Home, ChevronRight, ChevronLeft, Check, Loader2 } from "lucide-react";
import BrandHero from "@/components/Brand";
import { toast } from "sonner";

const ICONS = { Heart, Flame, Home };

export default function Onboarding() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [identity, setIdentity] = useState("companion");
  const [gender, setGender] = useState("female");
  const [origin, setOrigin] = useState("western");
  const [avatar, setAvatar] = useState(PORTRAITS.female.western);
  const [name, setName] = useState("");
  const [age, setAge] = useState(24);
  const [quiz, setQuiz] = useState({});

  const pickGender = (g) => { setGender(g); setAvatar(PORTRAITS[g][origin]); };
  const pickOrigin = (o) => { setOrigin(o); setAvatar(PORTRAITS[gender][o]); };

  const totalSteps = 4;
  const canNext = () => {
    if (step === 1) return !!name.trim();
    if (step === 2) return QUIZ.every((q) => quiz[q.key]);
    return true;
  };

  const finish = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/characters", {
        name: name.trim(), age: Number(age), identity, gender, origin,
        avatar_url: avatar, quiz,
      });
      toast.success(`${data.name} is ready for you`);
      nav("/app");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const next = () => { if (step < totalSteps - 1) setStep(step + 1); else finish(); };

  return (
    <div className="min-h-screen bg-ink grain relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full bg-brand/10 blur-[120px]" />
      <div className="relative z-10 max-w-3xl mx-auto px-6 py-10">
        {/* AFTERHELLO brand header */}
        <BrandHero className="mb-10" />

        {/* progress */}
        <div className="flex items-center gap-2 mb-10">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-brand" : "bg-white/10"}`} />
          ))}
        </div>

        {step === 0 && (
          <div className="animate-fade-up">
            <p className="text-xs uppercase tracking-[0.2em] text-brand mb-3">Step 1 — Identity</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-8">Who do you need?</h1>
            <div className="grid sm:grid-cols-3 gap-4">
              {IDENTITIES.map((it) => {
                const Icon = ICONS[it.icon];
                const active = identity === it.key;
                return (
                  <button key={it.key} data-testid={`identity-${it.key}`} onClick={() => setIdentity(it.key)}
                    className={`text-left p-6 rounded-3xl border transition-colors ${active ? "border-brand bg-brand/10" : "border-white/10 bg-surface hover:border-white/20"}`}>
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 ${active ? "bg-brand" : "bg-white/5"}`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg">{it.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{it.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="animate-fade-up">
            <p className="text-xs uppercase tracking-[0.2em] text-brand mb-3">Step 2 — Aesthetic</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-8">Shape their look.</h1>
            <div className="flex gap-2 mb-6">
              {["female", "male"].map((g) => (
                <button key={g} data-testid={`gender-${g}`} onClick={() => pickGender(g)}
                  className={`px-5 h-11 rounded-full text-sm font-medium capitalize transition-colors ${gender === g ? "bg-brand text-white" : "bg-surface border border-white/10 text-muted-foreground hover:text-white"}`}>
                  {g}
                </button>
              ))}
            </div>
            <div className="space-y-5">
              {ORIGINS.map((o) => (
                <div key={o.key}>
                  <p className="text-sm font-medium text-muted-foreground mb-2">{o.label}</p>
                  <button data-testid={`origin-${o.key}`} onClick={() => pickOrigin(o.key)}
                    className={`relative w-36 h-48 rounded-2xl overflow-hidden border-2 transition-colors ${origin === o.key ? "border-brand" : "border-transparent hover:border-white/20"}`}>
                    <img src={PORTRAITS[gender][o.key]} alt={o.label} className="w-full h-full object-cover" />
                    {origin === o.key && (
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-brand flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                  </button>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-4 mt-8">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Name</label>
                <Input data-testid="character-name-input" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Aria" className="h-13 bg-surface border-white/10 rounded-2xl h-14" />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Age</label>
                <Input data-testid="character-age-input" type="number" min={18} max={80} value={age}
                  onChange={(e) => setAge(e.target.value)} className="bg-surface border-white/10 rounded-2xl h-14" />
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="animate-fade-up">
            <p className="text-xs uppercase tracking-[0.2em] text-brand mb-3">Step 3 — Emotional Baseline</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-8">A few quick questions.</h1>
            <div className="space-y-6">
              {QUIZ.map((item, i) => (
                <div key={item.key}>
                  <p className="font-medium mb-3">{i + 1}. {item.q}</p>
                  <div className="flex flex-wrap gap-2">
                    {item.opts.map((opt) => (
                      <button key={opt} data-testid={`quiz-${item.key}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                        onClick={() => setQuiz((q) => ({ ...q, [item.key]: opt }))}
                        className={`px-4 h-11 rounded-full text-sm transition-colors ${quiz[item.key] === opt ? "bg-brand text-white" : "bg-surface border border-white/10 text-muted-foreground hover:text-white hover:border-white/20"}`}>
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="animate-fade-up text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-brand mb-3">Step 4 — Meet them</p>
            <div className="relative w-48 h-64 mx-auto rounded-3xl overflow-hidden border border-white/10 mb-6 brand-glow">
              <img src={avatar} alt={name} className="w-full h-full object-cover" />
            </div>
            <h1 className="font-heading text-4xl font-bold tracking-tight">{name || "Your companion"}</h1>
            <p className="text-muted-foreground mt-2">{age} • {IDENTITIES.find((x) => x.key === identity)?.title}</p>
          </div>
        )}

        {/* nav */}
        <div className="flex items-center justify-between mt-12">
          <Button variant="ghost" data-testid="onboarding-back" disabled={step === 0}
            onClick={() => setStep(step - 1)} className="rounded-full text-muted-foreground hover:text-white">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Button data-testid="onboarding-next" disabled={!canNext() || saving}
            onClick={next} className="h-12 px-7 rounded-full bg-brand hover:bg-brand-hover text-white font-semibold transition-colors brand-glow">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : step === totalSteps - 1 ? "Start chatting" : "Continue"}
            {!saving && <ChevronRight className="w-4 h-4 ml-1" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
